import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, CheckCircle, Clock, AlertTriangle, XCircle,
  RefreshCw, RotateCcw, ChevronDown, Filter, Search, FileCheck,
  Loader2, Target, Calendar, ScanLine, TrendingUp, Info
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { remediationTracking, workspaces as workspacesApi } from '../../services/api/client';
import RescanModal from '../../components/Remediation/RescanModal';
import CertificateModal from '../../components/Remediation/CertificateModal';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  open:          { labelKey: 'workspaces.statusOpen',        color: 'text-red-400',    bg: 'bg-red-500/15 border-red-500/30',    icon: XCircle },
  in_progress:   { labelKey: 'workspaces.statusInProgress',  color: 'text-yellow-400', bg: 'bg-yellow-500/15 border-yellow-500/30', icon: Clock },
  fixed:         { labelKey: 'workspaces.statusFixed',        color: 'text-blue-400',   bg: 'bg-blue-500/15 border-blue-500/30', icon: ScanLine },
  verified:      { labelKey: 'workspaces.statusVerified',     color: 'text-green-400',  bg: 'bg-green-500/15 border-green-500/30', icon: CheckCircle },
  accepted_risk: { labelKey: 'workspaces.statusAcceptedRisk', color: 'text-orange-400', bg: 'bg-orange-500/15 border-orange-500/30', icon: Shield },
  wont_fix:      { labelKey: 'workspaces.statusWontFix',      color: 'text-gray-400',   bg: 'bg-gray-500/15 border-gray-500/30', icon: Info },
};

const SEVERITY_CONFIG = {
  critical: { labelKey: 'workspaces.severityCritical', color: 'text-red-400',    dot: 'bg-red-500' },
  high:     { labelKey: 'workspaces.severityHigh',     color: 'text-orange-400', dot: 'bg-orange-500' },
  medium:   { labelKey: 'workspaces.severityMedium',   color: 'text-yellow-400', dot: 'bg-yellow-500' },
  low:      { labelKey: 'workspaces.severityLow',      color: 'text-blue-400',   dot: 'bg-blue-500' },
  info:     { labelKey: 'workspaces.severityInfo',     color: 'text-gray-400',   dot: 'bg-gray-500' },
};

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

// ── Sub-components ───────────────────────────────────────────────────────────

const KPICard = ({ label, value, icon: Icon, color, subtext }) => (
  <div className="bg-brand-black-light rounded-xl border border-gray-700 p-4 flex items-center gap-4">
    <div className={`p-2.5 rounded-lg ${color} bg-current/10`}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
      {subtext && <p className="text-xs text-gray-500 mt-0.5">{subtext}</p>}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${cfg.bg} ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {t(cfg.labelKey)}
    </span>
  );
};

const SeverityBadge = ({ severity }) => {
  const { t } = useI18n();
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {t(cfg.labelKey)}
    </span>
  );
};

const StatusDropdown = ({ currentStatus, vulnId, onUpdate }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSelect = async (newStatus) => {
    if (newStatus === currentStatus) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    try {
      await remediationTracking.updateStatus(vulnId, { remediation_status: newStatus });
      onUpdate(vulnId, newStatus);
      toast.success(t('workspaces.remediationStatusUpdate'));
    } catch {
      toast.error(t('workspaces.remediationStatusError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-600 text-xs text-gray-200 transition-colors"
      >
        <StatusBadge status={currentStatus} />
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-52 overflow-hidden">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => handleSelect(key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-700 transition-colors ${key === currentStatus ? 'bg-gray-700/50' : ''}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                  <span className={cfg.color}>{t(cfg.labelKey)}</span>
                  {key === currentStatus && <CheckCircle className="w-3 h-3 text-green-400 ml-auto" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const RemediationTracker = () => {
  const { t } = useI18n();
  usePageTitle('Remediation Tracker');
  const { id: workspaceId } = useParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [vulns, setVulns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vulnsLoading, setVulnsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [rescanVuln, setRescanVuln] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await remediationTracking.getDashboard(workspaceId);
      setDashboard(data);
    } catch {
      toast.error(t('workspaces.remediationLoadDashboardError'));
    }
  }, [workspaceId]);

  const fetchVulns = useCallback(async () => {
    setVulnsLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (filterStatus) params.remediation_status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;
      const data = await remediationTracking.getVulns(workspaceId, params);
      setVulns(data.vulnerabilities || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      toast.error(t('workspaces.remediationLoadVulnsError'));
    } finally {
      setVulnsLoading(false);
    }
  }, [workspaceId, page, filterStatus, filterSeverity]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const ws = await workspacesApi.get(workspaceId);
        setWorkspace(ws);
        await Promise.all([fetchDashboard(), fetchVulns()]);
      } catch {
        toast.error('Workspace introuvable');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [workspaceId]);

  useEffect(() => { fetchVulns(); }, [fetchVulns]);

  const handleStatusUpdate = (vulnId, newStatus) => {
    setVulns(prev => prev.map(v => v.id === vulnId ? { ...v, remediation_status: newStatus } : v));
    fetchDashboard();
  };

  const handleRescanDone = (vulnId, newStatus) => {
    setVulns(prev => prev.map(v => v.id === vulnId ? { ...v, remediation_status: newStatus } : v));
    fetchDashboard();
  };

  // Filtered vulns (client-side search)
  const displayed = search
    ? vulns.filter(v => v.title.toLowerCase().includes(search.toLowerCase()) || (v.cve_id || '').toLowerCase().includes(search.toLowerCase()))
    : vulns;

  if (loading) return (
    <div className="min-h-screen bg-brand-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-green-400" />
    </div>
  );

  const progress = dashboard?.progress_pct || 0;

  return (
    <div className="min-h-screen bg-brand-black text-white">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/workspaces/${workspaceId}`)} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-400" />
                {t('workspaces.remediationTitle')}
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">{workspace?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { fetchDashboard(); fetchVulns(); }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t('workspaces.remediationRefresh')}
            </button>
            <button
              onClick={() => setShowCertificate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium transition-colors"
            >
              <FileCheck className="w-4 h-4" />
              {t('workspaces.remediationCertificate')}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {dashboard && (
          <div className="bg-brand-black-light rounded-xl border border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-gray-200">{t('workspaces.remediationProgress')}</span>
              </div>
              <span className="text-2xl font-bold text-white">{progress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="h-3 rounded-full transition-all duration-700"
                style={{
                  width: `${progress}%`,
                  background: progress >= 80 ? '#22c55e' : progress >= 50 ? '#eab308' : '#ef4444'
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {dashboard.remediated_count} / {dashboard.total} {t('workspaces.remediationVulnsFixed')}
            </p>
          </div>
        )}

        {/* KPI Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPICard label={t('workspaces.kpiOpen')}         value={dashboard.open}          icon={XCircle}    color="text-red-400"    />
            <KPICard label={t('workspaces.kpiInProgress')}   value={dashboard.in_progress}   icon={Clock}      color="text-yellow-400" />
            <KPICard label={t('workspaces.kpiToVerify')}     value={dashboard.fixed}         icon={ScanLine}   color="text-blue-400"   />
            <KPICard label={t('workspaces.kpiVerified')}     value={dashboard.verified}      icon={CheckCircle} color="text-green-400" />
            <KPICard label={t('workspaces.kpiAcceptedRisk')} value={dashboard.accepted_risk} icon={Shield}     color="text-orange-400" />
            <KPICard label={t('workspaces.kpiTotal')}        value={dashboard.total}         icon={Target}     color="text-gray-400"   />
          </div>
        )}

        {/* Severity breakdown */}
        {dashboard?.by_severity && (
          <div className="bg-brand-black-light rounded-xl border border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-200 mb-4">{t('workspaces.remediationSeverityBreakdown')}</h3>
            <div className="space-y-3">
              {SEVERITY_ORDER.filter(s => dashboard.by_severity[s]).map(sev => {
                const data = dashboard.by_severity[sev];
                const total_sev = Object.values(data).reduce((a, b) => a + b, 0);
                const done = (data.verified || 0) + (data.accepted_risk || 0) + (data.wont_fix || 0);
                const pct = total_sev > 0 ? Math.round((done / total_sev) * 100) : 0;
                const cfg = SEVERITY_CONFIG[sev];
                return (
                  <div key={sev} className="flex items-center gap-3">
                    <div className="w-20 flex-shrink-0 flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                      <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <div className="flex-1 bg-gray-700 rounded-full h-2">
                      <div className={`h-2 rounded-full ${cfg.dot} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-gray-400 w-24 text-right">{done}/{total_sev} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top open critical */}
        {dashboard?.top_open_critical?.length > 0 && (
          <div className="bg-brand-black-light rounded-xl border border-red-500/20 p-5">
            <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('workspaces.remediationCritical')} ({dashboard.top_open_critical.length})
            </h3>
            <div className="space-y-2">
              {dashboard.top_open_critical.map(v => (
                <div key={v.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 min-w-0">
                    <SeverityBadge severity={v.severity} />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-200 truncate">{v.title}</p>
                      <p className="text-xs text-gray-500">{v.cve_id || 'No CVE'} · {v.host_ip}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {v.cvss_score && <span className="text-xs text-gray-400">CVSS {v.cvss_score}</span>}
                    <button
                      onClick={() => setRescanVuln(v)}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 border border-blue-500/30 rounded text-xs transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Re-scan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vulnerability table */}
        <div className="bg-brand-black-light rounded-xl border border-gray-700">
          {/* Filters */}
          <div className="p-4 border-b border-gray-700 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder={t('workspaces.remediationSearch')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-green-500"
            >
              <option value="">{t('workspaces.remediationAllStatuses')}</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{t(v.labelKey)}</option>
              ))}
            </select>
            <select
              value={filterSeverity}
              onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-green-500"
            >
              <option value="">{t('workspaces.remediationAllSeverities')}</option>
              {SEVERITY_ORDER.map(s => (
                <option key={s} value={s}>{t(SEVERITY_CONFIG[s].labelKey)}</option>
              ))}
            </select>
            <span className="flex items-center text-xs text-gray-500">{total} {t('workspaces.remediationResults')}</span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-3">{t('workspaces.remediationSeverity')}</th>
                  <th className="text-left px-4 py-3">{t('workspaces.remediationVulnerability')}</th>
                  <th className="text-left px-4 py-3">{t('workspaces.remediationHost')}</th>
                  <th className="text-left px-4 py-3">CVSS</th>
                  <th className="text-left px-4 py-3">{t('workspaces.remediationStatus')}</th>
                  <th className="text-left px-4 py-3">{t('workspaces.remediationUpdated')}</th>
                  <th className="text-left px-4 py-3">{t('workspaces.remediationActions')}</th>
                </tr>
              </thead>
              <tbody>
                {vulnsLoading ? (
                  <tr><td colSpan={7} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-500 mx-auto" /></td></tr>
                ) : displayed.length === 0 ? (
                  <tr><td colSpan={7} className="py-12 text-center text-gray-500 text-sm">{t('workspaces.remediationNoVulns')}</td></tr>
                ) : displayed.map(v => (
                  <tr key={v.id} className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3"><SeverityBadge severity={v.severity} /></td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-gray-200 truncate">{v.title}</p>
                      {v.cve_id && <p className="text-xs text-gray-500 mt-0.5">{v.cve_id}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 font-mono">{v.host_ip || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{v.cvss_score ?? '—'}</td>
                    <td className="px-4 py-3">
                      <StatusDropdown
                        currentStatus={v.remediation_status || 'open'}
                        vulnId={v.id}
                        onUpdate={handleStatusUpdate}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {v.remediation_updated_at
                        ? new Date(v.remediation_updated_at).toLocaleDateString('fr-FR')
                        : v.remediation_verified_at
                          ? <span className="text-green-400">✓ {new Date(v.remediation_verified_at).toLocaleDateString('fr-FR')}</span>
                          : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setRescanVuln(v)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 border border-blue-500/30 rounded-lg text-xs transition-colors"
                        title={t('workspaces.remediationRescanTitle')}
                      >
                        <RotateCcw className="w-3 h-3" />
                        Re-scan
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-700 flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 rounded-lg text-sm transition-colors"
              >
                {t('workspaces.remediationPrevious')}
              </button>
              <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 rounded-lg text-sm transition-colors"
              >
                {t('workspaces.remediationNext')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {rescanVuln && (
        <RescanModal
          vuln={rescanVuln}
          onClose={() => setRescanVuln(null)}
          onDone={handleRescanDone}
        />
      )}
      {showCertificate && (
        <CertificateModal
          workspaceId={workspaceId}
          dashboard={dashboard}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
};

export default RemediationTracker;
